// ワープテキスト: ラスタ化したテキストをメッシュで変形する (純ロジック)。
// Photoshop のワープと同じくスタイル + 曲げ (bend) + 水平 / 垂直方向のゆがみ。
// 出力はメッシュの各セルを 2 つの三角形として前方マッピングし、ソースをバイリニアで読む。
import type { RgbaImage } from '@/pixels/transform'
import type { Rect } from '@/types/geometry'
import type { TextWarp, WarpStyle } from '@/types/document'
import { renderMeshWarp } from '@/pixels/meshWarp'

export const WARP_STYLES: WarpStyle[] = ['arc', 'arcLower', 'arcUpper', 'bulge', 'flag', 'wave', 'fisheye', 'rise']

/** 正規化座標 (u, v ∈ [-1, 1]) の変位 (出力 = 入力 + 変位、単位はピクセル) */
export function warpDisplacement(style: WarpStyle, bend: number, u: number, v: number, w: number, h: number): { dx: number; dy: number } {
  const b = bend / 100
  const A = b * w * 0.25
  switch (style) {
    case 'arc': return { dx: 0, dy: A * (u * u - 1) }
    case 'arcLower': return { dx: 0, dy: A * (u * u - 1) * ((1 + v) / 2) }
    case 'arcUpper': return { dx: 0, dy: A * (u * u - 1) * ((1 - v) / 2) }
    case 'bulge': return { dx: 0, dy: b * h * 0.5 * (1 - u * u) * v }
    case 'flag': return { dx: 0, dy: A * 0.5 * Math.sin(Math.PI * u) }
    case 'wave': return { dx: 0, dy: A * 0.35 * Math.sin(Math.PI * 2 * u) }
    case 'fisheye': return { dx: b * w * 0.15 * u * (1 - v * v), dy: b * h * 0.5 * v * (1 - u * u) }
    case 'rise': return { dx: 0, dy: -A * u }
  }
}

/**
 * 画像をワープする。rect は文書座標での画像の位置。戻り値は新しい画像と矩形 (変形で広がる)。
 * 変位が無ければそのまま返す。
 */
export function warpRaster(image: RgbaImage, rect: Rect, warp: TextWarp): { image: RgbaImage; rect: Rect } {
  const w = image.width, h = image.height
  if ((!warp.bend && !warp.hDist && !warp.vDist) || w < 2 || h < 2) return { image, rect }
  const NX = Math.max(8, Math.min(48, Math.round(w / 8))), NY = Math.max(4, Math.min(24, Math.round(h / 8)))
  // メッシュ頂点: ソース座標 (sx, sy) と出力座標 (dx, dy)
  const pts: { sx: number; sy: number; dx: number; dy: number }[] = []
  for (let j = 0; j <= NY; j++) {
    for (let i = 0; i <= NX; i++) {
      const sx = (i / NX) * w, sy = (j / NY) * h
      const u = (i / NX) * 2 - 1, v = (j / NY) * 2 - 1
      const d = warpDisplacement(warp.style, warp.bend, u, v, w, h)
      // 水平 / 垂直方向のゆがみ (遠近風): 反対側の辺ほど縮む
      const hs = 1 + (warp.hDist / 100) * v * 0.5, vs = 1 + (warp.vDist / 100) * u * 0.5
      const cxp = w / 2, cyp = h / 2
      pts.push({ sx, sy, dx: cxp + (sx - cxp) * hs + d.dx, dy: cyp + (sy - cyp) * vs + d.dy })
    }
  }
  return renderMeshWarp(image, rect, pts, NX, NY)
}
